import pxToRem from "hooks/usePxToRem";

import {Grid} from "@mui/system";

import {DefaultCardStructure, ProcessDatePicker} from "components";
import {
  Modality,
  ModalityPayment,
  PlaceIssue,
  TypeKnowledge,
} from "util/globalEnums";
import {Controller, useFieldArray, useFormContext} from "react-hook-form";
import useOriginalOrAlternativeValue from "hooks/useOriginalOrAlternativeValue";
import {useState} from "react";
import ProcessTextField from "views/ProcessDetails/refactor/components/ProcessTextField";
import ProcessSelect from "views/ProcessDetails/refactor/components/ProcessSelect";
import {Process} from "views/ProcessDetails/refactor/types/process.types";
import {getProcessLabel} from "views/ProcessDetails/refactor/utils/processLabels";

const HouseInfo = () => {
  const {control, setValue, getValues} = useFormContext<Process>();

  //se tiver um tk house, pega o primeiro
  //se nao, pega o primeiro diferente de master

  /**
   * Se o house exite, retorna o index dele.
   * Se o house não existe, retorna o index do primeiro diferente de master.
   * Se o master não existe, retorna 1.
   */
  const getIndexOfHouse = () => {
    const indexOfHouse = getValues("knowledges")?.findIndex(
      (knowledge) => knowledge.typeKnowledge === "House",
    );
    const indexOfMaster = getValues("knowledges")?.findIndex(
      (knowledge) => knowledge.typeKnowledge === "Master",
    );

    if (indexOfMaster < 0) {
      return 1;
    }

    if (indexOfHouse < 0) {
      return getValues("knowledges")?.findIndex(
        (knowledge) => knowledge.typeKnowledge !== "Master",
      );
    }
    return indexOfHouse;
  };

  /**
   * Caso o house não exista, o indexOfHouse será 1.
   */
  const houseIndex = getIndexOfHouse();

  const {fields: houseBl} = useFieldArray({
    control,
    keyName: "formId",
    name: "knowledges",
  });

  const {valueOrEmptyString} = useOriginalOrAlternativeValue();

  const [allowHouseIsRequired, setAllowHouseIsRequired] = useState(false);

  const checkIfHouseIsRequired = () => {
    if (
      getValues(`knowledges.${houseIndex}`)?.dateDeconsolidation ||
      getValues(`knowledges.${houseIndex}`)?.placeIssue ||
      getValues(`knowledges.${houseIndex}`)?.logisticBillLading
        ?.descriptionOfPackages
    ) {
      setAllowHouseIsRequired(true);
    } else {
      setAllowHouseIsRequired(false);
    }
  };

  const [dateDeconsolidationIsDisabled, setDateDeconsolidationIsDisabled] =
    useState(!getValues(`knowledges.${houseIndex}`)?.number);

  return (
    <DefaultCardStructure
      title={getProcessLabel("knowledge.house.title", getValues("modality"))}
      hasDivider={false}
      isSubcard
    >
      {[houseBl[houseIndex]].map((house) => {
        return (
          <Grid
            container
            rowSpacing={pxToRem(20)}
            columnSpacing={pxToRem(8)}
            key={`${house.formId}`}
            sx={{mb: pxToRem(8)}}
          >
            <Grid size={2.4}>
              <Controller
                name={`knowledges.${houseIndex}.number` as const}
                control={control}
                render={({field: {value}}) => (
                  <ProcessTextField
                    fullWidth
                    label={getProcessLabel(
                      "knowledge.house.number",
                      getValues("modality"),
                    )}
                    size="small"
                    placeholder={getProcessLabel(
                      "knowledge.house.number",
                      getValues("modality"),
                    )}
                    name={`knowledges.${houseIndex}.number`}
                    value={valueOrEmptyString(value ?? "")}
                    onBlur={() => {
                      checkIfHouseIsRequired();
                    }}
                    required={allowHouseIsRequired}
                    onChange={(e) => {
                      setDateDeconsolidationIsDisabled(false);
                      setValue(
                        `knowledges.${houseIndex}.number`,
                        e.target.value,
                        {
                          shouldDirty: true,
                        },
                      );
                      setValue(
                        `knowledges[${houseIndex}].typeKnowledge` as `knowledges.0.typeKnowledge`,
                        TypeKnowledge.House,
                        {
                          shouldDirty: true,
                        },
                      );
                    }}
                  />
                )}
              />
            </Grid>

            <Grid size={2.4}>
              <Controller
                name={`knowledges.${houseIndex}.paymentMethod`}
                control={control}
                render={({field: {onChange, value}}) => (
                  <ProcessSelect
                    name={`knowledges.${houseIndex}.paymentMethod`}
                    value={valueOrEmptyString(value ?? "")}
                    onChange={onChange}
                    data={Object.values(ModalityPayment)}
                    size="small"
                    disabled
                    fullWidth
                    label="Modelo De Pagamento"
                    onBlur={() => {
                      checkIfHouseIsRequired();
                    }}
                  />
                )}
              />
            </Grid>

            <Grid size={2.4}>
              <Controller
                name={`knowledges.${houseIndex}.deconsolidationNumber`}
                control={control}
                render={({field: {onChange, value}}) => (
                  <ProcessTextField
                    fullWidth
                    label="CE n°"
                    name={`knowledges.${houseIndex}.deconsolidationNumber`}
                    value={valueOrEmptyString(value ?? "")}
                    placeholder="CE n°"
                    onChange={onChange}
                    size="small"
                    disabled
                    onBlur={() => {
                      checkIfHouseIsRequired();
                    }}
                  />
                )}
              />
            </Grid>

            <Grid size={2.4}>
              <Controller
                name={`knowledges.${houseIndex}.dateDeconsolidation`}
                control={control}
                render={({field: {value}}) => (
                  <ProcessDatePicker
                    value={value}
                    name={`knowledges.${houseIndex}.dateDeconsolidation`}
                    title="Data CE"
                    disabled={
                      dateDeconsolidationIsDisabled ||
                      getValues("modality") === Modality.Aérea
                    }
                    fullWidth
                    label="Data CE"
                  />
                )}
              />
            </Grid>

            <Grid size={2.4}>
              <Controller
                name={`knowledges.${houseIndex}.placeIssue`}
                control={control}
                render={({field: {onChange, value}}) => (
                  <ProcessSelect
                    name={`knowledges.${houseIndex}.placeIssue`}
                    value={valueOrEmptyString(value ?? "")}
                    onChange={onChange}
                    data={Object.values(PlaceIssue)}
                    size="small"
                    fullWidth
                    label="Local Emissão"
                    onBlur={() => {
                      checkIfHouseIsRequired();
                    }}
                    disabled={getValues("modality") === Modality.Aérea}
                  />
                )}
              />
            </Grid>
            <Grid size={12}>
              <Controller
                name={
                  `knowledges.${houseIndex}.logisticBillLading.descriptionOfPackages` as const
                }
                control={control}
                render={({field: {value}}) => (
                  <ProcessTextField
                    multiline
                    minRows={4}
                    maxRows={8}
                    size="small"
                    fullWidth
                    label="Descrição dos pacotes"
                    name={`knowledges.${houseIndex}.logisticBillLading.descriptionOfPackages`}
                    value={valueOrEmptyString(value)}
                    placeholder="descrição"
                    onBlur={() => {
                      checkIfHouseIsRequired();
                    }}
                    onChange={(e) => {
                      if (e?.target?.value) {
                        setValue(
                          `knowledges.${houseIndex}.logisticBillLading.descriptionOfPackages` as const,
                          e.target.value,
                          {shouldDirty: true},
                        );
                      } else {
                        setValue(
                          `knowledges.${houseIndex}.logisticBillLading.descriptionOfPackages` as const,
                          undefined,
                          {shouldDirty: true},
                        );
                      }
                    }}
                    id="Descrição dos pacotes"
                    disabled={getValues("modality") === Modality.Aérea}
                  />
                )}
              />
            </Grid>
          </Grid>
        );
      })}
    </DefaultCardStructure>
  );
};

export default HouseInfo;
